import React, { useEffect, useState } from 'react';
import { Plus, Search } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { UserTable } from '../components/users/UserTable';
import { UserForm } from '../components/users/UserForm';
import { ApiService } from '../services/api';
import { AuthUser, CreateUserForm, UpdateUserForm } from '../types';
import toast from 'react-hot-toast';

export const Users: React.FC = () => {
  const [users, setUsers] = useState<AuthUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState<AuthUser | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [nextCursor, setNextCursor] = useState<Date | undefined>(undefined);
  const [hasNext, setHasNext] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async (cursor?: Date) => {
    try {
      setLoading(true);
      const result = await ApiService.getUsers({
        page: 1,
        limit: 50,
        startAfterDate: cursor,
      });
      setUsers(cursor ? (prev) => [...prev, ...result.data] : result.data);
      setHasNext(result.pagination.hasNext);
      const last = result.data[result.data.length - 1];
      setNextCursor(last ? last.createdAt : undefined);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    if (!hasNext || !nextCursor) return;
    try {
      setLoadingMore(true);
      await loadUsers(nextCursor);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load more users');
    } finally {
      setLoadingMore(false);
    }
  };

  const handleCreateUser = async (data: CreateUserForm) => {
    try {
      setFormLoading(true);
      const result = await ApiService.createUser(data);
      
      if (result.success) {
        toast.success('User created successfully');
        setShowForm(false);
        await loadUsers();
      } else {
        toast.error(result.error || 'Failed to create user');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to create user');
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateUser = async (data: UpdateUserForm) => {
    if (!editingUser) return;

    try {
      setFormLoading(true);
      const result = await ApiService.updateUser(editingUser.id, data);
      
      if (result.success) {
        toast.success('User updated successfully');
        setEditingUser(null);
        await loadUsers();
      } else {
        toast.error(result.error || 'Failed to update user');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to update user');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteUser = async (user: AuthUser) => {
    if (!confirm(`Are you sure you want to delete ${user.firstName} ${user.lastName}?`)) {
      return;
    }

    try {
      const result = await ApiService.deleteUser(user.id);
      
      if (result.success) {
        toast.success('User deleted successfully');
        await loadUsers();
      } else {
        toast.error(result.error || 'Failed to delete user');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete user');
    }
  };

  const handleToggleStatus = async (user: AuthUser) => {
    try {
      const result = await ApiService.updateUser(user.id, {
        firstName: user.firstName,
        lastName: user.lastName,
        phoneNumber: user.phoneNumber,
        role: user.role,
        isActive: !user.isActive,
      });
      
      if (result.success) {
        toast.success(`User ${user.isActive ? 'deactivated' : 'activated'} successfully`);
        await loadUsers();
      } else {
        toast.error(result.error || 'Failed to update user status');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to update user status');
    }
  };

  const handleEditUser = (user: AuthUser) => {
    setEditingUser(user);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingUser(null);
  };

  const filteredUsers = users.filter(user =>
    user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.phoneNumber.includes(searchTerm)
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Users</h1>
          <p className="text-secondary-600">Manage user accounts and permissions</p>
        </div>
        <Button
          onClick={() => setShowForm(true)}
          leftIcon={<Plus className="h-4 w-4" />}
        >
          Add User
        </Button>
      </div>

      <div className="flex items-center space-x-4">
        <div className="flex-1 max-w-md">
          <Input
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            leftIcon={<Search className="h-4 w-4" />}
          />
        </div>
      </div>

      <UserTable
        users={filteredUsers}
        onEdit={handleEditUser}
        onDelete={handleDeleteUser}
        onToggleStatus={handleToggleStatus}
        loading={loading}
      />

      {hasNext && (
        <div className="flex justify-center">
          <Button variant="outline" onClick={loadMore} loading={loadingMore} disabled={loadingMore}>
            Load more
          </Button>
        </div>
      )}

      {(showForm || editingUser) && (
        <UserForm
          user={editingUser || undefined}
          onSubmit={editingUser ? (data: any) => handleUpdateUser(data as UpdateUserForm) : (data: any) => handleCreateUser(data as CreateUserForm)}
          onCancel={handleCloseForm}
          loading={formLoading}
        />
      )}
    </div>
  );
};
